import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useGetAdminEnquiryQuery } from "@/store/adminService";
import { formatAmount } from "@/util/sums";
import {
  buyingWithOptions,
  employmentOptions,
  optionLabel,
  ownershipOptions,
  propertyTypeOptions,
  timeframeOptions,
} from "../GetStarted/enquiryOptions";

const money = (value: number) => formatAmount(String(value));

const BackLink = () => (
  <Link
    to="/admin/enquiries"
    className="inline-flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-900"
  >
    <ArrowLeft className="size-4" /> All enquiries
  </Link>
);

export default function AdminEnquiry() {
  const { id = "" } = useParams();
  const { data: enquiry, isLoading, isError } = useGetAdminEnquiryQuery(id);

  if (isLoading) {
    return <div className="text-center py-8 text-gray-500">Loading enquiry...</div>;
  }

  if (isError || !enquiry) {
    return (
      <div>
        <BackLink />
        <p className="mt-6 text-gray-500">This enquiry couldn’t be found.</p>
      </div>
    );
  }

  const sections: { title: string; rows: [string, React.ReactNode][] }[] = [
    {
      title: "Contact",
      rows: [
        [
          "Email",
          <a href={`mailto:${enquiry.email}`} className="text-blue-700 hover:underline">
            {enquiry.email}
          </a>,
        ],
        [
          "Phone",
          <a href={`tel:${enquiry.phone}`} className="text-blue-700 hover:underline">
            {enquiry.phone}
          </a>,
        ],
      ],
    },
    {
      title: "Situation",
      rows: [
        ["Buying", optionLabel(buyingWithOptions, enquiry.buyingWith)],
        ["Employment", optionLabel(employmentOptions, enquiry.employmentStatus)],
        ["Combined annual income", money(enquiry.annualIncome)],
        ["Deposit savings", money(enquiry.depositSavings)],
        ["Property owned", optionLabel(ownershipOptions, enquiry.propertyOwnership)],
        ["Monthly debt repayments", money(enquiry.monthlyDebts)],
      ],
    },
    {
      title: "Goals",
      rows: [
        ["Looking in", enquiry.location],
        ["Target price", money(enquiry.targetPrice)],
        ["Property type", optionLabel(propertyTypeOptions, enquiry.propertyType)],
        ["Timeframe", optionLabel(timeframeOptions, enquiry.timeframe)],
        ["Expected loan approval", money(enquiry.loanApproval)],
        ["What matters most", enquiry.priorities || "—"],
      ],
    },
  ];

  return (
    <div>
      <BackLink />
      <div className="mt-4 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-2xl font-semibold text-gray-900">
          {enquiry.firstName} {enquiry.lastName}
        </h2>
        <p className="text-sm text-gray-500">
          Received {new Date(enquiry.createdAt).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
        </p>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        {sections.map((section) => (
          <section key={section.title} className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider">{section.title}</h3>
            <dl className="mt-4 space-y-3">
              {section.rows.map(([label, value]) => (
                <div key={label}>
                  <dt className="text-xs text-gray-500">{label}</dt>
                  <dd className="text-sm text-gray-900 break-words">{value}</dd>
                </div>
              ))}
            </dl>
          </section>
        ))}
      </div>
    </div>
  );
}
